import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';

export interface HUDConfig {
  type: 'success' | 'error';
  message?: string;
  duration?: number;
}

export function showHUD(config: HUDConfig) {
  const event = new CustomEvent('show-hud', { detail: config });
  window.dispatchEvent(event);
}

export default function NotificationHUD() {
  const [active, setActive] = useState(false);
  const [config, setConfig] = useState<HUDConfig | null>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [hudId, setHudId] = useState(0);

  useEffect(() => {
    const handleShow = (e: Event) => {
      const customEvent = e as CustomEvent<HUDConfig>;
      setConfig(customEvent.detail);
      setActive(true);
      setIsFadingOut(false);
      setHudId((prev) => prev + 1);
    };

    window.addEventListener('show-hud', handleShow);
    return () => window.removeEventListener('show-hud', handleShow);
  }, []);

  useEffect(() => {
    if (!active || !config) return;

    const duration = config.duration || 1800; // 1.8 seconds

    const fadeTimer = setTimeout(() => {
      setIsFadingOut(true);
    }, duration - 300);

    const hideTimer = setTimeout(() => {
      setActive(false);
      setConfig(null);
      setIsFadingOut(false);
    }, duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [active, config]);

  if (!active || !config) return null;

  const isSuccess = config.type === 'success';
  const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <div
        key={hudId}
        className="glass-panel"
        style={{
          width: '260px',
          height: '180px',
          borderRadius: '24px',
          backgroundColor: 'rgba(26, 28, 31, 0.9)',
          border: '1px solid rgba(231, 228, 223, 0.15)',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          animation: !animationsEnabled
            ? 'none'
            : isFadingOut
            ? 'hudFadeOut 0.3s cubic-bezier(0.25, 1, 0.5, 1) forwards'
            : 'hudFadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          pointerEvents: 'auto',
        }}
      >
        {isSuccess ? (
          <div
            style={{
              position: 'relative',
              width: '64px',
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
            }}
          >
            {/* Spinning indicator ring */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: animationsEnabled
                  ? '2px solid rgba(91, 161, 139, 0.2)'
                  : '2px solid var(--color-success)',
                borderTopColor: 'var(--color-success)',
                borderRadius: '50%',
                animation: animationsEnabled ? 'hudSpin 1.2s linear infinite' : 'none',
              }}
            />
            {/* Check Icon */}
            <Check
              size={32}
              color="var(--color-success)"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                animation: animationsEnabled
                  ? 'hudCheckScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both'
                  : 'none',
              }}
            />
          </div>
        ) : (
          <div
            style={{
              position: 'relative',
              width: '64px',
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
            }}
          >
            {/* Red transparent background ring */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '2px solid var(--color-error)',
                borderRadius: '50%',
                opacity: 0.3,
              }}
            />
            <X
              size={36}
              color="var(--color-error)"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                animation: animationsEnabled ? 'hudShake 0.4s ease-in-out' : 'none',
              }}
            />
          </div>
        )}

        <span
          style={{
            fontFamily: 'var(--font-heading-en)',
            fontSize: '20px',
            fontWeight: '600',
            color: isSuccess ? 'var(--color-success)' : 'var(--color-error)',
            letterSpacing: '1px',
          }}
        >
          {isSuccess ? 'Success' : 'Error'}
        </span>

        {config.message && (
          <span
            style={{
              fontSize: '12px',
              color: 'var(--color-text)',
              opacity: 0.8,
              marginTop: '8px',
              textAlign: 'center',
              padding: '0 16px',
              display: 'block',
              whiteSpace: 'pre-line',
              lineHeight: '1.4',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {config.message}
          </span>
        )}
      </div>
    </div>
  );
}
